import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../admin/AuthContext';
import { 
  GET_MY_DELIVERY_ADDRESSES, 
  CREATE_DELIVERY_ADDRESS, 
  UPDATE_DELIVERY_ADDRESS, 
  DELETE_DELIVERY_ADDRESS,
  SET_DEFAULT_DELIVERY_ADDRESS 
} from '../apollo/queries/deliveryAddressQueries';
import './css/DeliveryAddressModal.css';

const DeliveryAddressModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newAddress, setNewAddress] = useState({
    name: '',
    recipient: '',
    phone: '',
    zipCode: '',
    address: '',
    addressDetail: '',
    isDefault: false
  });

  // 배송지 목록 조회
  const { data: addressData, loading: addressLoading, refetch: refetchAddresses } = useQuery(GET_MY_DELIVERY_ADDRESSES, {
    skip: !isOpen,
    errorPolicy: 'all'
  });

  // 배송지 생성 뮤테이션
  const [createDeliveryAddress, { loading: creating }] = useMutation(CREATE_DELIVERY_ADDRESS, {
    onCompleted: () => {
      setIsAddingNew(false);
      setNewAddress({
        name: '',
        recipient: '',
        phone: '',
        zipCode: '',
        address: '',
        addressDetail: '',
        isDefault: false
      });
      refetchAddresses();
      alert('배송지가 추가되었습니다.');
    },
    onError: (error) => {
      console.error('배송지 추가 에러:', error);
      alert('배송지 추가 중 오류가 발생했습니다.');
    }
  });

  // 배송지 삭제 뮤테이션
  const [deleteDeliveryAddress] = useMutation(DELETE_DELIVERY_ADDRESS, {
    onCompleted: () => {
      refetchAddresses();
      alert('배송지가 삭제되었습니다.');
    },
    onError: (error) => {
      console.error('배송지 삭제 에러:', error);
      alert('배송지 삭제 중 오류가 발생했습니다.');
    }
  });

  // 기본 배송지 설정 뮤테이션
  const [setDefaultDeliveryAddress] = useMutation(SET_DEFAULT_DELIVERY_ADDRESS, {
    onCompleted: () => {
      refetchAddresses();
      alert('기본 배송지가 설정되었습니다.');
    },
    onError: (error) => {
      console.error('기본 배송지 설정 에러:', error);
      alert('기본 배송지 설정 중 오류가 발생했습니다.');
    }
  });

  const addresses = addressData?.getMyDeliveryAddresses || [];

  // Daum 주소검색 초기화
  useEffect(() => {
    if (isAddressSearchOpen && window.daum && window.daum.Postcode) {
      const element_layer = document.getElementById('address_search_layer');
      
      new window.daum.Postcode({
        oncomplete: function(data) {
          // 팝업에서 검색결과 항목을 클릭했을때 실행할 코드를 작성하는 부분.
          let addr = ''; // 주소 변수
          let extraAddr = ''; // 참고항목 변수

          // 사용자가 선택한 주소 타입에 따라 해당 주소 값을 가져온다.
          if (data.userSelectedType === 'R') { // 사용자가 도로명 주소를 선택했을 경우
            addr = data.roadAddress;
          } else { // 사용자가 지번 주소를 선택했을 경우(J)
            addr = data.jibunAddress;
          }

          // 사용자가 선택한 주소가 도로명 타입일때 참고항목을 조합한다.
          if(data.userSelectedType === 'R'){
            // 법정동명이 있을 경우 추가한다. (법정리는 제외)
            // 법정동의 경우 마지막 문자가 "동/로/가"로 끝난다.
            if(data.bname !== '' && /[동|로|가]$/g.test(data.bname)){
              extraAddr += data.bname;
            }
            // 건물명이 있고, 공동주택일 경우 추가한다.
            if(data.buildingName !== '' && data.apartment === 'Y'){
              extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
            }
            // 표시할 참고항목이 있을 경우, 괄호까지 추가한 최종 문자열을 만든다.
            if(extraAddr !== ''){
              extraAddr = ' (' + extraAddr + ')';
            }
          }

          setNewAddress(prev => ({
            ...prev,
            zipCode: data.zonecode,
            address: addr + extraAddr
          }));

          // 주소 선택 후 주소검색 레이어 닫기
          setIsAddressSearchOpen(false);
        },
        onresize: function(size) {
          element_layer.style.height = size.height + 'px';
        },
        width: '100%',
        height: '100%'
      }).embed(element_layer);

      // 주소검색 레이어 표시
      element_layer.style.display = 'block';
    }
  }, [isAddressSearchOpen]);

  const handleAddressSearch = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert('주소 검색 서비스를 불러올 수 없습니다.');
      return;
    }

    setIsAddressSearchOpen(true);
  };

  const handleCloseAddressSearch = () => {
    setIsAddressSearchOpen(false);
    const element_layer = document.getElementById('address_search_layer');
    if (element_layer) {
      element_layer.style.display = 'none';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddAddress = async () => {
    if (!newAddress.name.trim() || !newAddress.recipient.trim() || 
        !newAddress.phone.trim() || !newAddress.zipCode.trim() || 
        !newAddress.address.trim()) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    try {
      await createDeliveryAddress({
        variables: {
          input: {
            name: newAddress.name,
            recipient: newAddress.recipient,
            phone: newAddress.phone,
            zipCode: newAddress.zipCode,
            address: newAddress.address,
            addressDetail: newAddress.addressDetail || '',
            isDefault: newAddress.isDefault
          }
        }
      });
    } catch (error) {
      console.error('배송지 추가 에러:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (addresses.find(addr => addr.id === addressId)?.isDefault) {
      alert('기본 배송지는 삭제할 수 없습니다.');
      return;
    }
    
    if (window.confirm('선택한 배송지를 삭제하시겠습니까?')) {
      try {
        await deleteDeliveryAddress({
          variables: { id: addressId }
        });
      } catch (error) {
        console.error('배송지 삭제 에러:', error);
      }
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await setDefaultDeliveryAddress({
        variables: { id: addressId }
      });
    } catch (error) {
      console.error('기본 배송지 설정 에러:', error);
    }
  };

  const handleModalClose = () => {
    setIsAddingNew(false);
    setIsAddressSearchOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="delivery-modal-overlay" onClick={handleModalClose}>
      <div className="delivery-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delivery-modal-header">
          <h2>배송지 관리</h2>
          <button onClick={handleModalClose} className="delivery-modal-close">&times;</button>
        </div>
        
        <div className="delivery-modal-content">
          {/* 주소 검색 레이어 */}
          {isAddressSearchOpen && (
            <div className="address-search-overlay">
              <div className="address-search-container">
                <div className="address-search-header">
                  <h3>주소 검색</h3>
                  <button 
                    onClick={handleCloseAddressSearch} 
                    className="address-search-close"
                  >
                    &times;
                  </button>
                </div>
                <div 
                  id="address_search_layer" 
                  className="address-search-content"
                  style={{ display: 'none' }}
                ></div>
              </div>
            </div>
          )}

          {!isAddingNew ? (
            <>
              <div className="delivery-modal-actions">
                <button 
                  onClick={() => setIsAddingNew(true)} 
                  className="add-address-btn"
                >
                  새 배송지 추가
                </button>
              </div>
              
              <div className="address-list">
                {addressLoading ? (
                  <div className="loading-message">배송지 정보를 불러오는 중...</div>
                ) : addresses.length === 0 ? (
                  <div className="empty-address-list">
                    <p>등록된 배송지가 없습니다.</p>
                    {user && (user.zipCode || user.address) && (
                      <div className="user-default-address">
                        <h4>회원 정보 기본 주소</h4>
                        <div className="address-item default-user-address">
                          <div className="address-info">
                            <div className="address-name">기본 주소 (회원정보)</div>
                            <div className="address-recipient">{user.name}</div>
                            <div className="address-phone">{user.phone || user.mobile || '-'}</div>
                            <div className="address-full">
                              {user.zipCode && `[${user.zipCode}] `}
                              {user.address}
                              {user.addressDetail && ` ${user.addressDetail}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  addresses.map(address => (
                    <div key={address.id} className="address-item">
                      <div className="address-info">
                        <div className="address-name">
                          {address.name}
                          {address.isDefault && <span className="default-badge">기본</span>}
                        </div>
                        <div className="address-recipient">{address.recipient}</div>
                        <div className="address-phone">{address.phone}</div>
                        <div className="address-full">
                          [{address.zipCode}] {address.address}
                          {address.addressDetail && ` ${address.addressDetail}`}
                        </div>
                      </div>
                      
                      <div className="address-actions">
                        {!address.isDefault && (
                          <button 
                            onClick={() => handleSetDefault(address.id)}
                            className="set-default-btn"
                          >
                            기본설정
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteAddress(address.id)}
                          className="delete-address-btn"
                          disabled={address.isDefault}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="add-address-form">
              <h3>새 배송지 추가</h3>
              
              <div className="form-group">
                <label>배송지명 *</label>
                <input
                  type="text"
                  name="name"
                  value={newAddress.name}
                  onChange={handleInputChange}
                  placeholder="예: 집, 회사, 기타"
                />
              </div>
              
              <div className="form-group">
                <label>받는 분 *</label>
                <input
                  type="text"
                  name="recipient"
                  value={newAddress.recipient}
                  onChange={handleInputChange}
                  placeholder="받는 분 성함"
                />
              </div>
              
              <div className="form-group">
                <label>연락처 *</label>
                <input
                  type="tel"
                  name="phone"
                  value={newAddress.phone}
                  onChange={handleInputChange}
                  placeholder="연락처"
                />
              </div>
              
              <div className="form-group">
                <label>주소 *</label>
                <div className="address-search-group">
                  <input
                    type="text"
                    name="zipCode"
                    value={newAddress.zipCode}
                    readOnly
                    placeholder="우편번호"
                    className="zipcode-input"
                  />
                  <button 
                    type="button" 
                    onClick={handleAddressSearch}
                    className="address-search-btn"
                  >
                    주소찾기
                  </button>
                </div>
                <input
                  type="text"
                  name="address"
                  value={newAddress.address}
                  readOnly
                  placeholder="기본주소"
                  className="address-input"
                />
                <input
                  type="text"
                  name="addressDetail"
                  value={newAddress.addressDetail}
                  onChange={handleInputChange}
                  placeholder="상세주소"
                  className="address-detail-input"
                />
              </div>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={newAddress.isDefault}
                    onChange={handleInputChange}
                  />
                  기본 배송지로 설정
                </label>
              </div>
              
              <div className="form-actions">
                <button 
                  onClick={() => setIsAddingNew(false)}
                  className="cancel-btn"
                >
                  취소
                </button>
                <button 
                  onClick={handleAddAddress}
                  className="save-btn"
                >
                  저장
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryAddressModal; 